import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { getSubscribers } from '@/lib/api';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Loader2, Users, Mail, Bell, BellOff, Download, Send, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import AdminLayout from '@/components/AdminLayout';

const AdminSubscribers = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pushStats, setPushStats] = useState(0);
  const [pushSubs, setPushSubs] = useState([]);

  // Push notification form
  const [pushTitle, setPushTitle] = useState('');
  const [pushBody, setPushBody] = useState('');
  const [pushUrl, setPushUrl] = useState('/');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) { navigate('/admin'); return; }
    fetchSubscribers();
    fetchPushStats();
  }, [isAuthenticated, navigate]);

  const fetchSubscribers = async () => {
    try {
      const response = await getSubscribers();
      setSubscribers(response.data);
    } catch (error) {
      toast.error('Failed to fetch subscribers');
    } finally {
      setLoading(false);
    }
  };

  const fetchPushStats = async () => {
    try {
      const res = await api.get('/admin/push/stats');
      setPushStats(res.data.subscribers || 0);
      const subsRes = await api.get('/admin/push/subscribers');
      setPushSubs(subsRes.data || []);
    } catch (e) {}
  };

  const exportCSV = () => {
    if (!subscribers.length) return;
    const header = 'Name,Email,Push Notifications,Subscribed At\n';
    const rows = subscribers.map(s =>
      `"${s.name || ''}","${s.email}","${s.push_enabled ? 'Yes' : 'No'}","${s.subscribed_at || ''}"`
    ).join('\n');
    const blob = new Blob([header + rows], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `subscribers_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const sendPush = async () => {
    if (!pushTitle.trim()) {
      toast.error('Enter a title');
      return;
    }
    setSending(true);
    try {
      const res = await api.post('/admin/push/send', {
        title: pushTitle,
        body: pushBody || 'Tap to read',
        url: pushUrl || '/',
      });
      toast.success(`Push sent to ${res.data.sent} subscribers! (${res.data.failed} failed)`);
      setPushTitle('');
      setPushBody('');
      setPushUrl('/');
    } catch (error) {
      toast.error('Failed to send push notification');
    } finally {
      setSending(false);
    }
  };

  if (!isAuthenticated) return null;

  const emailPushCount = subscribers.filter(s => s.push_enabled).length;

  return (
    <AdminLayout>
      <div data-testid="admin-subscribers-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white">Subscribers</h2>
            <p className="text-gray-500 mt-1">{subscribers.length} email · {pushStats} browser push</p>
          </div>
          {subscribers.length > 0 && (
            <button
              onClick={exportCSV}
              className="flex items-center gap-2 px-4 py-2 border border-[#333] text-gray-300 hover:text-white hover:border-white text-sm transition-colors"
              data-testid="export-csv-btn"
            >
              <Download size={16} />
              Export CSV
            </button>
          )}
        </div>

        {/* Push Notification Panel */}
        <div className="bg-[#111] border border-yellow-500/20 p-5 mb-6" data-testid="push-panel">
          <div className="flex items-center gap-2 mb-4">
            <Bell size={18} className="text-yellow-500" />
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Send Push Notification</h3>
            <span className="ml-auto text-xs text-gray-500">{pushStats} subscribers</span>
          </div>

          <div className="space-y-3">
            <input
              type="text"
              value={pushTitle}
              onChange={(e) => setPushTitle(e.target.value)}
              placeholder="Notification title..."
              className="w-full px-3 py-2.5 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500"
              data-testid="push-title-input"
            />
            <input
              type="text"
              value={pushBody}
              onChange={(e) => setPushBody(e.target.value)}
              placeholder="Message body (optional)..."
              className="w-full px-3 py-2.5 bg-black border border-[#222] text-white text-sm focus:outline-none focus:border-yellow-500"
              data-testid="push-body-input"
            />
            <input
              type="text"
              value={pushUrl}
              onChange={(e) => setPushUrl(e.target.value)}
              placeholder="Link URL (e.g. /article/slug-here)"
              className="w-full px-3 py-2.5 bg-black border border-[#222] text-gray-400 text-sm focus:outline-none focus:border-yellow-500"
              data-testid="push-url-input"
            />
            <button
              onClick={sendPush}
              disabled={!pushTitle.trim() || sending}
              className="w-full sm:w-auto flex items-center justify-center gap-2 px-6 py-3 bg-yellow-500 text-white font-bold hover:bg-yellow-600 transition-colors disabled:opacity-50"
              data-testid="send-push-btn"
            >
              {sending ? (
                <><Loader2 size={16} className="animate-spin" /> Sending...</>
              ) : (
                <><Send size={16} /> Send to All</>
              )}
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
          <div className="bg-[#111] border border-[#222] p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-1">
              <Users size={14} /> Email
            </div>
            <p className="text-2xl font-bold text-white">{subscribers.length}</p>
          </div>
          <div className="bg-[#111] border border-[#222] p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-1">
              <Bell size={14} /> Browser Push
            </div>
            <p className="text-2xl font-bold text-green-500">{pushStats}</p>
          </div>
          <div className="bg-[#111] border border-[#222] p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-1">
              <Bell size={14} /> Email + Push
            </div>
            <p className="text-2xl font-bold text-yellow-500">{emailPushCount}</p>
          </div>
          <div className="bg-[#111] border border-[#222] p-4">
            <div className="flex items-center gap-2 text-gray-500 text-xs uppercase tracking-wider mb-1">
              <Mail size={14} /> Email Only
            </div>
            <p className="text-2xl font-bold text-gray-400">{subscribers.length - emailPushCount}</p>
          </div>
        </div>

        {/* Email Subscriber List */}
        <div className="bg-[#111] border border-[#222]">
          <div className="px-4 py-3 border-b border-[#222]">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Email Subscribers</h3>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-yellow-500" />
            </div>
          ) : subscribers.length > 0 ? (
            <div className="divide-y divide-[#222]">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-[#0a0a0a] text-[10px] uppercase tracking-wider text-gray-600">
                <div className="col-span-2">Name</div>
                <div className="col-span-3">Email</div>
                <div className="col-span-2">Push</div>
                <div className="col-span-2">From</div>
                <div className="col-span-2">Date</div>
                <div className="col-span-1">Actions</div>
              </div>

              {subscribers.map((sub, idx) => (
                <div key={sub.id || idx} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 px-4 py-3 hover:bg-[#0a0a0a] transition-colors" data-testid={`subscriber-row-${idx}`}>
                  <div className="sm:col-span-2 text-white text-sm font-medium truncate">
                    {sub.name || <span className="text-gray-600 italic">No name</span>}
                  </div>
                  <div className="sm:col-span-3 text-gray-400 text-sm truncate">{sub.email}</div>
                  <div className="sm:col-span-2">
                    <button
                      onClick={async () => {
                        try {
                          const res = await api.put(`/admin/newsletter/${encodeURIComponent(sub.email)}/toggle-push`);
                          setSubscribers(prev => prev.map(s => s.email === sub.email ? { ...s, push_enabled: res.data.push_enabled } : s));
                          toast.success(`Push ${res.data.push_enabled ? 'enabled' : 'disabled'} for ${sub.email}`);
                        } catch (e) { toast.error('Failed to toggle'); }
                      }}
                      className="inline-flex items-center gap-1 text-xs transition-colors hover:opacity-80"
                      data-testid={`toggle-push-${idx}`}
                    >
                      {sub.push_enabled ? (
                        <><ToggleRight size={18} className="text-green-500" /> <span className="text-green-500">On</span></>
                      ) : (
                        <><ToggleLeft size={18} className="text-gray-600" /> <span className="text-gray-600">Off</span></>
                      )}
                    </button>
                  </div>
                  <div className="sm:col-span-2 text-gray-500 text-xs flex items-center truncate">
                    {sub.source_page || '—'}
                  </div>
                  <div className="sm:col-span-2 text-gray-600 text-xs flex items-center">
                    {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
                  </div>
                  <div className="sm:col-span-1 flex items-center">
                    <button
                      onClick={async () => {
                        if (!window.confirm(`Remove ${sub.email}?`)) return;
                        try {
                          await api.delete(`/admin/newsletter/${encodeURIComponent(sub.email)}`);
                          setSubscribers(prev => prev.filter(s => s.email !== sub.email));
                          toast.success('Subscriber removed');
                        } catch (e) { toast.error('Failed to remove'); }
                      }}
                      className="text-gray-600 hover:text-yellow-500 transition-colors"
                      data-testid={`delete-sub-${idx}`}
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-12 text-center">
              <Users size={48} className="mx-auto text-gray-700 mb-4" />
              <h3 className="text-lg font-bold text-white">No subscribers yet</h3>
              <p className="text-gray-500 text-sm mt-2">Subscribers will appear here when people sign up</p>
            </div>
          )}
        </div>

        {/* Browser Push Subscribers */}
        <div className="bg-[#111] border border-[#222] mt-6">
          <div className="px-4 py-3 border-b border-[#222]">
            <h3 className="text-sm font-bold text-gray-400 uppercase tracking-wider">Browser Push Subscribers ({pushSubs.length})</h3>
          </div>
          {pushSubs.length > 0 ? (
            <div className="divide-y divide-[#222]">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-4 py-2 bg-[#0a0a0a] text-[10px] uppercase tracking-wider text-gray-600">
                <div className="col-span-1">#</div>
                <div className="col-span-6">Endpoint</div>
                <div className="col-span-2">Status</div>
                <div className="col-span-3">Subscribed</div>
              </div>
              {pushSubs.map((sub, idx) => (
                <div key={idx} className="grid grid-cols-1 sm:grid-cols-12 gap-1 sm:gap-2 px-4 py-3 hover:bg-[#0a0a0a] transition-colors" data-testid={`push-sub-row-${idx}`}>
                  <div className="sm:col-span-1 text-gray-500 text-sm">{idx + 1}</div>
                  <div className="sm:col-span-6 text-gray-400 text-xs truncate">{sub.endpoint?.slice(0, 60) || 'Unknown'}...</div>
                  <div className="sm:col-span-2">
                    <span className={`text-xs ${sub.active !== false ? 'text-green-500' : 'text-gray-600'}`}>
                      {sub.active !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="sm:col-span-3 text-gray-600 text-xs">
                    {sub.created_at ? new Date(sub.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <Bell size={32} className="mx-auto text-gray-700 mb-3" />
              <p className="text-gray-500 text-sm">No browser push subscribers yet</p>
              <p className="text-gray-600 text-xs mt-1">Users subscribe via the push banner on the site</p>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminSubscribers;
